import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, Unlock, AlertTriangle, ArrowRightLeft, History } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Badge, Btn } from "@/components/ui-kit";
import api from "@/lib/api";
import Modal from "@/pages/ops/Modal";

export const isAvailable = (c) => c.active !== false && !c.account_suspended;

export function ClinicianStatusBadges({ c }) {
  return (
    <>
      {c.account_suspended && <span data-testid={`clin-suspended-badge-${c.id}`}><Badge tone="rose">Suspended</Badge></span>}
      {!c.account_suspended && c.active === false && <span data-testid={`clin-inactive-badge-${c.id}`}><Badge tone="slate">Inactive</Badge></span>}
    </>
  );
}

function ReassignModal({ c, onClose, onDone }) {
  const [list, setList] = useState([]);
  const [shifts, setShifts] = useState(null);
  const [sel, setSel] = useState({});
  const [bulk, setBulk] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/ops/clinicians").then((r) => setList(r.data.filter((x) => x.id !== c.id && x.credential_status === "Verified" && isAvailable(x))));
    api.get(`/ops/clinicians/${c.id}`).then((r) => {
      const today = new Date(Date.now() + 330 * 60000).toISOString().slice(0, 10);
      setShifts(r.data.roster.filter((s) => s.date >= today).sort((a, b) => `${a.date}${a.start}`.localeCompare(`${b.date}${b.start}`)));
    });
  }, [c.id]);
  const picked = Object.entries(sel).filter(([, v]) => v.on);
  const toggle = (id, on) => setSel({ ...sel, [id]: { ...(sel[id] || {}), on } });
  const setTarget = (id, to) => setSel({ ...sel, [id]: { on: true, to } });
  const allOn = shifts && shifts.length > 0 && shifts.every((s) => sel[s.id]?.on);
  const toggleAll = (on) => setSel(Object.fromEntries((shifts || []).map((s) => [s.id, { ...(sel[s.id] || {}), on }])));
  const applyBulk = (to) => {
    setBulk(to);
    if (to) setSel(Object.fromEntries((shifts || []).map((s) => [s.id, sel[s.id]?.on ? { on: true, to } : (sel[s.id] || { on: false })])));
  };
  const missingTarget = picked.some(([, v]) => !v.to);
  const go = async () => {
    if (!picked.length) return toast.error("Select at least one shift");
    if (missingTarget) return toast.error("Pick a clinician for every selected shift");
    setBusy(true);
    try {
      const { data } = await api.post(`/ops/clinicians/${c.id}/reassign-shifts`, { moves: picked.map(([id, v]) => ({ shift_id: id, to_clinician_id: v.to })) });
      toast.success(`${data.moved} shift${data.moved === 1 ? "" : "s"} moved — ${data.per_target.map((t) => `${t.count} to ${t.name}`).join(", ")}`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not reassign"); }
    finally { setBusy(false); }
  };
  const fmt = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
  return (
    <Modal wide title="Reassign upcoming shifts" subtitle={`Pick which of ${c.name}'s upcoming shifts to move — you can send each one to a different clinician`} onClose={onClose}>
      {list.length === 0 && <p className="text-[11.5px] text-amber-600 mb-3" data-testid="reassign-no-targets">No other active, verified clinicians available.</p>}
      {!shifts ? <p className="text-[12px] text-slate-400">Loading shifts…</p> : (
        <>
          <div className="flex items-center gap-3 flex-wrap mb-3">
            <label className="inline-flex items-center gap-2 text-[12.5px] font-semibold text-slate-700">
              <input type="checkbox" checked={allOn} onChange={(e) => toggleAll(e.target.checked)} data-testid="reassign-select-all" /> Select all ({shifts.length})
            </label>
            <select className="pq-input py-1.5 w-full sm:w-72 sm:ml-auto" value={bulk} onChange={(e) => applyBulk(e.target.value)} data-testid="reassign-bulk-target">
              <option value="">Send all selected to…</option>
              {list.map((x) => <option key={x.id} value={x.id}>{`${x.name} · ${x.upcoming_shifts || 0} upcoming`}</option>)}
            </select>
          </div>
          <div className="space-y-1.5 max-h-[50vh] overflow-y-auto">
            {shifts.map((s) => (
              <div key={s.id} className={`flex items-center gap-2.5 flex-wrap rounded-lg px-3 py-2 text-[12.5px] ${sel[s.id]?.on ? "bg-teal-50 border border-teal-200" : "bg-slate-50 border border-transparent"}`} data-testid={`reassign-shift-${s.id}`}>
                <input type="checkbox" checked={!!sel[s.id]?.on} onChange={(e) => toggle(s.id, e.target.checked)} data-testid={`reassign-check-${s.id}`} />
                <span className="font-semibold whitespace-nowrap">{fmt(s.date)} · {s.start}–{s.end}</span>
                <span className="text-slate-600 truncate min-w-0">{s.patient_name || "General shift"} · {s.location}</span>
                <select className="pq-input py-1 w-full sm:w-56 sm:ml-auto" value={sel[s.id]?.to || ""} onChange={(e) => setTarget(s.id, e.target.value)} data-testid={`reassign-target-${s.id}`}>
                  <option value="">Move to…</option>
                  {list.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                </select>
              </div>
            ))}
          </div>
        </>
      )}
      <div className="flex items-center gap-2 mt-5 flex-wrap">
        <span className="text-[12px] text-slate-500" data-testid="reassign-summary">{picked.length} of {shifts?.length || 0} selected{missingTarget ? " · pick a clinician for each" : ""}</span>
        <div className="flex gap-2 ml-auto">
          <Btn onClick={onClose} data-testid="reassign-cancel">Cancel</Btn>
          <Btn variant="primary" onClick={go} disabled={busy || !picked.length || missingTarget} data-testid="reassign-confirm"><ArrowRightLeft size={14} /> {busy ? "Moving…" : `Move ${picked.length || ""} shift${picked.length === 1 ? "" : "s"}`}</Btn>
        </div>
      </div>
    </Modal>
  );
}

export function ShiftWarning({ c, onChange }) {
  const [open, setOpen] = useState(false);
  if (isAvailable(c) || !c.upcoming_shifts) return null;
  return (
    <div className="flex items-center gap-1.5 flex-wrap text-[11.5px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5" onClick={(e) => e.stopPropagation()} data-testid={`clin-shift-warning-${c.id}`}>
      <AlertTriangle size={13} className="shrink-0" />
      <span>{c.upcoming_shifts} upcoming shift{c.upcoming_shifts === 1 ? " needs" : "s need"} reassigning</span>
      <button onClick={() => setOpen(true)} className="ml-auto inline-flex items-center gap-1 rounded-md bg-white border border-amber-300 px-2 py-0.5 font-semibold hover:bg-amber-100" data-testid={`clin-reassign-btn-${c.id}`}>
        <ArrowRightLeft size={12} /> Reassign
      </button>
      {open && <ReassignModal c={c} onClose={() => setOpen(false)} onDone={() => { setOpen(false); onChange && onChange(); }} />}
    </div>
  );
}

const LOG_TONE = { Activated: "emerald", Deactivated: "slate", Suspended: "rose", Reinstated: "emerald", "Shifts reassigned": "amber" };

export function StatusLog({ log = [] }) {
  return (
    <div className="rounded-xl border border-slate-100 p-3 mb-4" data-testid="clin-status-log">
      <div className="pq-label mb-2 flex items-center gap-1.5"><History size={12} /> Status history</div>
      {log.length === 0 ? <p className="text-[12px] text-slate-400" data-testid="clin-status-log-empty">No status changes recorded yet.</p> : (
        <div className="space-y-1.5 max-h-48 overflow-y-auto">
          {log.map((l) => (
            <div key={l.id} className="flex items-center gap-2 flex-wrap text-[12px]" data-testid={`clin-status-log-${l.id}`}>
              <Badge tone={LOG_TONE[l.action] || "slate"}>{l.action}</Badge>
              {l.detail && <span className="text-slate-600">{l.detail}</span>}
              <span className="text-slate-500 sm:ml-auto">by <b className="text-slate-700">{l.by_name || "Unknown"}</b> · {new Date(l.at).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ClinicianStatusControls({ c, onChange }) {
  const [busy, setBusy] = useState(false);
  const update = async (body, msg) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/ops/clinicians/${c.id}/status`, body);
      toast.success(msg);
      if (!isAvailable(data) && data.upcoming_shifts) toast.warning(`${data.upcoming_shifts} upcoming shift${data.upcoming_shifts === 1 ? "" : "s"} for ${data.name} ${data.upcoming_shifts === 1 ? "needs" : "need"} reassigning`);
      onChange && onChange(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update status"); }
    finally { setBusy(false); }
  };
  const suspend = () => {
    if (c.account_suspended) return update({ suspended: false }, `${c.name} reinstated — login restored`);
    if (!window.confirm(`Suspend ${c.name}? They will be logged out and locked out of the clinician portal until reinstated.`)) return;
    update({ suspended: true }, `${c.name} suspended — portal access locked`);
  };
  return (
    <div className="flex items-center gap-3 flex-wrap" onClick={(e) => e.stopPropagation()} data-testid={`clin-status-${c.id}`}>
      <label className="inline-flex items-center gap-2 text-[12px] font-semibold text-slate-600">
        <Switch checked={c.active !== false} disabled={busy || c.account_suspended}
          onCheckedChange={(v) => update({ active: v }, `${c.name} ${v ? "activated" : "deactivated"}`)}
          data-testid={`clin-active-switch-${c.id}`} />
        {c.active !== false ? "Active" : "Inactive"}
      </label>
      <button onClick={suspend} disabled={busy} data-testid={`clin-suspend-btn-${c.id}`}
        className={`inline-flex items-center gap-1 rounded-lg border px-2.5 py-1 text-[11.5px] font-semibold transition-colors disabled:opacity-50 ${c.account_suspended ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : "border-rose-200 text-rose-600 hover:bg-rose-50"}`}>
        {c.account_suspended ? <><Unlock size={12} /> Reinstate</> : <><Lock size={12} /> Suspend</>}
      </button>
    </div>
  );
}
